import React from 'react';
import {
  BMSVoucherGuest,
  BMSVoucherHideableText,
  VoucherHotel,
  VoucherTransfer,
  VoucherGroundService,
  VoucherExperience,
  ProcessedUpload,
} from 'services/ItineraryManagerApi/types';
import { HeaderCollapsible } from 'ui/HeaderCollapsible';
import { SvgIcon } from 'ui/SvgIcon';
import { ArticleIcon } from 'ui/Icons/components/Article.component';
import { theme } from '../../../tailwind.config';
import { formatDateDisplay, isBlank } from 'utils';
import { Table } from 'ui/Table';
import { ESortOrder } from 'store/common/types';
import { ExpandableTable } from 'ui/ExpandableTable/ExpandableTable';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import { SeeMoreCollapsible } from 'ui/SeeMoreCollapsible';
import { sortHotelsByEarliestDate } from './utils';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { ProductViewOverFold, AllocatedGuests, shouldRenderHideableText } from './components/common';
import { VoucherAnnouncementsRenderer } from './components/VoucherAnnouncementsRenderer';

// Shared component for rendering uploads/attachments
interface IUploadsRendererProps {
  uploads: any[];
}

const UploadsRenderer: React.FC<IUploadsRendererProps> = ({ uploads }) => {
  const params = useDynamicParameters();
  const { ITINERARY_MANAGER_BASE_URL } = params.dynamicParameters;

  if (!uploads || uploads.length === 0) return null;

  // Filter to only show file uploads
  const fileUploads = uploads.filter(upload => upload.fileType === 'file');

  if (fileUploads.length === 0) return null;

  return (
    <HeaderCollapsible titleClassname="font-pt-sans text-sm font-normal" title="ATTACHMENTS" childrenClassname="mt-2">
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {fileUploads.map((upload, uploadIndex) => (
          <a
            key={uploadIndex}
            href={`${ITINERARY_MANAGER_BASE_URL}/viewer/v1/media/${upload.path}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 cursor-pointer group"
          >
            <SvgIcon
              IconComponent={ArticleIcon}
              defaultFill={theme.colors['brown-100']}
              hoverFill={theme.colors['brown-hard']}
              defaultBackground={theme.colors['transparent']}
              hoverBackground={theme.colors['gray-20']}
              width="16px"
              height="16px"
            />
            <span className="font-pt-sans text-brown-prime underline text-sm cursor-pointer group-hover:text-brown-hard">
              {upload.displayName || `Attachment ${uploadIndex + 1}`}
            </span>
          </a>
        ))}
      </div>
    </HeaderCollapsible>
  );
};

interface IVoucherHotelsRendererProps {
  hotels: VoucherHotel[];
  allocations: { guestUuid: string; breakdownItemUuid: string }[];
  guests: BMSVoucherGuest[];
}

export const VoucherHotelsRenderer: React.FC<IVoucherHotelsRendererProps> = ({ hotels, allocations, guests }) => {
  if (!hotels || hotels.length === 0) return null;

  // Filter hotels to only show those with visible accommodations
  const hotelsWithVisibleAccommodations = hotels.filter(hotel =>
    hotel.accommodations.some(accommodation => accommodation.isVisible)
  );

  if (hotelsWithVisibleAccommodations.length === 0) return null;

  return (
    <div className="flex flex-col">
      <h3 className="m-0 font-pt-sans my-0 mb-4 w-full text-brown-prime pb-2 border-b border-brown-prime uppercase text-17px font-normal">
        HOTELS
      </h3>

      {sortHotelsByEarliestDate(hotelsWithVisibleAccommodations).map(hotel => {
        return (
          <div key={hotel.uuid} className="mb-8">
            {/* Hotel Header */}
            <div className="mb-6">
              <h4 className="m-0 font-noe-display text-2xl font-normal mb-2 text-brown-prime">{hotel.name}</h4>
              <p className="m-0 font-pt-sans text-brown-prime text-15px">{hotel.address}</p>
              <p className="m-0 font-pt-sans text-brown-prime text-15px">
                {hotel.phoneNumber && hotel.isPhoneVisible !== false && <span>+{hotel.phoneNumber}</span>}
                {hotel.phoneNumber && hotel.isPhoneVisible !== false && hotel.email && hotel.isEmailVisible !== false && <span> / </span>}
                {hotel.email && hotel.isEmailVisible !== false && (
                  <a href={`mailto:${hotel.email}`} className="underline text-brown-prime">
                    {hotel.email}
                  </a>
                )}
              </p>
            </div>

            {/* Room Details */}
            {hotel.accommodations
              .filter(accommodation => accommodation.isVisible)
              .map((accommodation, accIndex) => {
                // Calculate nights for this specific accommodation
                const accStartDate = new Date(accommodation.startDate);
                const accEndDate = new Date(accommodation.endDate);
                const accNights = Math.ceil((accEndDate.getTime() - accStartDate.getTime()) / (1000 * 60 * 60 * 24));

                return (
                  <div key={accommodation.uuid} className="mb-6">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-3 pb-3 border-b border-gray-20">
                      <div className="flex flex-col md:block md:self-end">
                        <p className="m-0 font-pt-sans text-black text-20px font-bold mb-2 md:mb-0">
                          {accommodation.title}
                        </p>
                        {/* Confirmation Number */}
                        {!isBlank(accommodation.confirmationNumber?.trim()) && (
                          <div className="md:hidden">
                            <span className="font-pt-sans text-13px text-gray-100 bg-white-hard py-1">
                              Confirm N. {accommodation.confirmationNumber}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="md:text-right">
                        {/* Confirmation Number - Desktop */}
                        {!isBlank(accommodation.confirmationNumber?.trim()) && (
                          <p className="hidden md:inline-block m-0 font-pt-sans text-13px text-gray-100 mb-2 bg-white-hard py-1">
                            Confirm N. {accommodation.confirmationNumber}
                          </p>
                        )}
                        <p className="m-0 font-pt-sans text-gray-100 text-sm">
                          {formatDateDisplay(accommodation.startDate)} - {formatDateDisplay(accommodation.endDate)}
                          <span className="ml-2 bg-teal-20 text-black px-2 py-1 text-xs rounded">
                            {accNights} Nights
                          </span>
                          <span className="ml-4">
                            {accommodation.numberOfAdults &&
                              `${accommodation.numberOfAdults} x ${
                                accommodation.numberOfAdults > 1 ? 'Adults' : 'Adult'
                              }`}
                            {accommodation.agesOfAllChildren &&
                              accommodation.agesOfAllChildren.length > 0 &&
                              `, ${accommodation.agesOfAllChildren.length} x ${
                                accommodation.agesOfAllChildren.length > 1 ? 'Children' : 'Child'
                              }`}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Room Details */}
                    <div className="mb-3">
                      <p className="m-0 font-pt-sans font-bold mb-2 text-brown-prime text-15px">Room Details:</p>
                      {accommodation.description && (
                        <SeeMoreCollapsible cutoffHeightPx={150}>
                          <p className="m-0 font-pt-sans text-15px text-flint">
                            <HtmlStringRenderer className="m-0" content={accommodation.description} />
                          </p>
                        </SeeMoreCollapsible>
                      )}
                    </div>

                    {/* Attachments */}
                    <UploadsRenderer uploads={accommodation.uploads} />

                    {/* Rate Inclusions */}
                    {accommodation.isRateFeaturesVisible &&
                      accommodation.rateFeatures &&
                      accommodation.rateFeatures.length > 0 && (
                        <div className="mt-10px">
                          <p className="m-0 font-pt-sans mb-5px text-brown-prime text-15px font-bold">
                            Rate Inclusions:
                          </p>
                          <ul className="m-0 pl-20px flex flex-col gap-10px">
                            {accommodation.rateFeatures.map((feature, index) => (
                              <li key={index} className="font-pt-sans text-15px text-black">
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Meal Plans */}
                    {accommodation.mealPlans && accommodation.mealPlans.length > 0 && (
                      <div className="mt-3">
                        {accommodation.mealPlans.map(mealPlan => (
                          <div key={mealPlan.uuid} className="mb-3">
                            <p className="text-15px m-0 font-pt-sans font-medium mb-1 text-brown-prime">
                              <span className="font-bold">
                                Meal Plan: <span className="font-normal">{mealPlan.name}</span>
                              </span>
                            </p>
                            {mealPlan.description && (
                              <>
                                <span className="block text-15px text-brown-prime font-pt-sans font-bold mt-10px">
                                  Meal Plan Details
                                </span>
                                <SeeMoreCollapsible cutoffHeightPx={150}>
                                  <p className="m-0 font-pt-sans text-15px text-flint mb-2">
                                    {<HtmlStringRenderer className="m-0" content={mealPlan.description} />}
                                  </p>
                                </SeeMoreCollapsible>
                              </>
                            )}
                            <UploadsRenderer uploads={mealPlan.uploads} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Supplements */}
                    {accommodation.supplements && accommodation.supplements.filter(sup => sup.isVisible).length > 0 && (
                      <div className="mt-3">
                        <p className="m-0 font-pt-sans mb-2 text-brown-prime text-15px font-bold">Supplements:</p>
                        {accommodation.supplements
                          .filter(sup => sup.isVisible)
                          .map(supplement => (
                            <div key={supplement.uuid} className="mb-2">
                              <p className="m-0 font-pt-sans text-15px text-black">
                                <span className="text-brown-prime">{supplement.title}</span>
                                {supplement.tertiaryText && <HtmlStringRenderer content={supplement.tertiaryText} />}
                              </p>
                            </div>
                          ))}
                      </div>
                    )}

                    {shouldRenderHideableText(accommodation.supplementsMoreInfo) && (
                      <div className="mt-10px">
                        <HeaderCollapsible
                          titleClassname="font-pt-sans text-15px font-normal"
                          title="Supplements More Info"
                          childrenClassname="mt-5px"
                        >
                          <p className="m-0 font-pt-sans text-15px text-black whitespace-pre-wrap">
                            <HtmlStringRenderer className="m-0" content={accommodation.supplementsMoreInfo!.text} />
                          </p>
                        </HeaderCollapsible>
                      </div>
                    )}

                    {/* Additional Hotel Services */}
                    {accommodation.additionalHotelServices && accommodation.additionalHotelServices.filter(ahs => ahs.isVisible).length > 0 && (
                      <div className="mt-3">
                        <p className="m-0 font-pt-sans mb-2 text-brown-prime text-15px font-bold">
                          Additional Hotel Services:
                        </p>
                        {accommodation.additionalHotelServices
                          .filter(ahs => ahs.isVisible)
                          .map(service => (
                            <div key={service.uuid} className="mb-2">
                              <p className="m-0 font-pt-sans text-15px text-black">
                                <span className="text-brown-prime">{service.title}</span>
                                {service.tertiaryText && <HtmlStringRenderer content={service.tertiaryText} />}
                              </p>
                            </div>
                          ))}
                      </div>
                    )}

                    {shouldRenderHideableText(accommodation.additionalHotelServicesMoreInfo) && (
                      <div className="mt-10px">
                        <HeaderCollapsible
                          titleClassname="font-pt-sans text-15px font-normal"
                          title="Additional Hotel Services More Info"
                          childrenClassname="mt-5px"
                        >
                          <p className="m-0 font-pt-sans text-15px text-black whitespace-pre-wrap">
                            <HtmlStringRenderer
                              className="m-0"
                              content={accommodation.additionalHotelServicesMoreInfo!.text}
                            />
                          </p>
                        </HeaderCollapsible>
                      </div>
                    )}

                    {/* More Info */}
                    {accommodation.moreText && (
                      <HeaderCollapsible
                        titleClassname="font-pt-sans text-15px font-normal"
                        title="MORE INFO"
                        childrenClassname="mt-2"
                      >
                        <p className="m-0 font-pt-sans text-15px text-gray-100 whitespace-pre-wrap">
                          <HtmlStringRenderer className="m-0" content={accommodation.moreText} />
                        </p>
                      </HeaderCollapsible>
                    )}

                    {/* Allocated Guests */}
                    <AllocatedGuests 
                      allocations={allocations}
                      guests={guests}
                      productUuid={accommodation.uuid}
                    />
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};

interface IVoucherGuestsRendererProps {
  guests: BMSVoucherGuest[];
  guestsMoreInfo?: BMSVoucherHideableText;
}

interface IVoucherTransfersRendererProps {
  transfers: VoucherTransfer[];
  allocations: { guestUuid: string; breakdownItemUuid: string }[];
  guests: BMSVoucherGuest[];
}

interface IVoucherGroundServicesRendererProps {
  groundServices: VoucherGroundService[];
  allocations: { guestUuid: string; breakdownItemUuid: string }[];
  guests: BMSVoucherGuest[];
}

interface IVoucherExperiencesRendererProps {
  experiences: VoucherExperience[];
  allocations: { guestUuid: string; breakdownItemUuid: string }[];
  guests: BMSVoucherGuest[];
}

export const VoucherExperiencesRenderer: React.FC<IVoucherExperiencesRendererProps> = ({ experiences, allocations, guests }) => {
  if (!experiences || experiences.length === 0) return null;

  // Filter only visible experiences
  const visibleExperiences = experiences.filter(experience => experience.isVisible);
  if (visibleExperiences.length === 0) return null;

  return (
    <div className="flex flex-col mb-40px">
      <h3 className="m-0 font-pt-sans my-0 mb-4 w-full text-brown-prime pb-2 border-b border-brown-prime uppercase text-17px font-normal">
        EXPERIENCES
      </h3>

      <div className="flex flex-col gap-20px">
        {visibleExperiences.map(experience => {
          return (
            <div key={experience.uuid}>
              <ProductViewOverFold product={experience} />

              {/* Border after tertiary text */}
              <div className="border-b border-gray-20"></div>

              {/* Description */}
              {experience.description && (
                <div className="mt-10px mb-3">
                  <SeeMoreCollapsible cutoffHeightPx={150}>
                    <p className="m-0 font-pt-sans text-15px text-flint">
                      <HtmlStringRenderer className="m-0" content={experience.description} />
                    </p>
                  </SeeMoreCollapsible>
                </div>
              )}

              {/* Attachments */}
              <UploadsRenderer uploads={experience.uploads} />

              {/* More Info */}
              {experience.moreText && (
                <HeaderCollapsible
                  titleClassname="font-pt-sans text-15px font-normal"
                  title="MORE INFO"
                  childrenClassname="mt-2"
                >
                  <p className="m-0 font-pt-sans text-15px text-gray-100 whitespace-pre-wrap">
                    <HtmlStringRenderer className="m-0" content={experience.moreText} />
                  </p>
                </HeaderCollapsible>
              )}

              {/* Allocated Guests */}
              <AllocatedGuests 
                allocations={allocations}
                guests={guests}
                productUuid={experience.uuid}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const VoucherGroundServicesRenderer: React.FC<IVoucherGroundServicesRendererProps> = ({ groundServices, allocations, guests }) => {
  if (!groundServices || groundServices.length === 0) return null;

  // Filter only visible ground services
  const visibleGroundServices = groundServices.filter(service => service.isVisible);
  if (visibleGroundServices.length === 0) return null;

  return (
    <div className="flex flex-col mb-40px">
      <h3 className="m-0 font-pt-sans my-0 mb-4 w-full text-brown-prime pb-2 border-b border-brown-prime uppercase text-17px font-normal">
        GROUND SERVICES
      </h3>

      <div className="flex flex-col gap-20px">
        {visibleGroundServices.map(service => {
          return (
            <div key={service.uuid}>
              <ProductViewOverFold product={service} />

              {/* Border after title */}
              <div className="border-b border-gray-20"></div>

              {/* Description */}
              {service.description && (
                <div className="mt-10px mb-3">
                  <SeeMoreCollapsible cutoffHeightPx={150}>
                    <p className="m-0 font-pt-sans text-15px text-flint">
                      <HtmlStringRenderer className="m-0" content={service.description} />
                    </p>
                  </SeeMoreCollapsible>
                </div>
              )}

              {/* Attachments */}
              <UploadsRenderer uploads={service.uploads} />

              {/* More Info */}
              {service.moreText && (
                <HeaderCollapsible
                  titleClassname="font-pt-sans text-15px font-normal"
                  title="MORE INFO"
                  childrenClassname="mt-2"
                >
                  <p className="m-0 font-pt-sans text-15px text-gray-100 whitespace-pre-wrap">
                    <HtmlStringRenderer className="m-0" content={service.moreText} />
                  </p>
                </HeaderCollapsible>
              )}

              {/* Allocated Guests */}
              <AllocatedGuests 
                allocations={allocations}
                guests={guests}
                productUuid={service.uuid}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const VoucherTransfersRenderer: React.FC<IVoucherTransfersRendererProps> = ({ transfers, allocations, guests }) => {
  if (!transfers || transfers.length === 0) return null;

  // Filter only visible transfers
  const visibleTransfers = transfers.filter(transfer => transfer.isVisible);
  if (visibleTransfers.length === 0) return null;

  return (
    <div className="flex flex-col mb-40px">
      <h3 className="m-0 font-pt-sans my-0 mb-4 w-full text-brown-prime pb-2 border-b border-brown-prime uppercase text-17px font-normal">
        Transfers
      </h3>

      <div className="flex flex-col gap-20px">
        {visibleTransfers.map(transfer => {
          return (
            <div key={transfer.uuid}>
              <ProductViewOverFold product={transfer} />

              {/* Border after tertiary text */}
              <div className="border-b border-gray-20"></div>

              {/* Description */}
              {transfer.description && (
                <div className="mt-10px mb-3">
                  <SeeMoreCollapsible cutoffHeightPx={150}>
                    <p className="m-0 font-pt-sans text-15px text-flint">
                      <HtmlStringRenderer className="m-0" content={transfer.description} />
                    </p>
                  </SeeMoreCollapsible>
                </div>
              )}

              {/* Attachments */}
              <UploadsRenderer uploads={transfer.uploads} />

              {/* More Info */}
              {transfer.moreText && (
                <HeaderCollapsible
                  titleClassname="font-pt-sans text-15px font-normal"
                  title="MORE INFO"
                  childrenClassname="mt-2"
                >
                  <p className="m-0 font-pt-sans text-15px text-gray-100 whitespace-pre-wrap">
                    <HtmlStringRenderer className="m-0" content={transfer.moreText} />
                  </p>
                </HeaderCollapsible>
              )}

              {/* Allocated Guests */}
              <AllocatedGuests 
                allocations={allocations}
                guests={guests}
                productUuid={transfer.uuid}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const VoucherGuestsRenderer: React.FC<IVoucherGuestsRendererProps> = ({ guests, guestsMoreInfo }) => {
  if (!guests || guests.length === 0) return null;

  // Filter only visible guests
  const visibleGuests = guests.filter(guest => guest.isVisible);

  if (visibleGuests.length === 0) return null;

  // Format time to display properly
  const formatTime = (time?: string) => {
    if (!time) return '-';
    return time;
  };

  // Format arrival/departure for expandable table
  const formatArrivalDeparture = (date?: string, time?: string, flightNumber?: string) => {
    const hasDate = !!date;
    const hasTime = !!time;
    const hasFlight = !!flightNumber;

    if (!hasDate && !hasFlight) return '-';

    let result = '';

    // Add date and time if available
    if (hasDate) {
      result = formatDateDisplay(date);
      if (hasTime) {
        result += `, ${formatTime(time)}`;
      }
    }

    // Add flight number if available
    if (hasFlight) {
      if (result) {
        result += '   ';
      }
      result += `Flight N. ${flightNumber}`;
    }

    return result;
  };

  // Format guest name
  const formatGuestName = (guest: any) => {
    const parts = [guest.title, guest.firstName, guest.lastName].filter(Boolean);
    return parts.join(' ') || '-';
  };

  // Calculate guest age based on dob or fallback to manualAge
  const calculateGuestAge = (guest: BMSVoucherGuest) => {
    if (guest.dob) {
      // Calculate age from date of birth using current date
      const today = new Date();
      const birthDate = new Date(guest.dob);
      const age = Math.floor((today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
      return age.toString();
    }
    if (guest.manualAge) {
      return guest.manualAge.toString();
    }
    return '-';
  };

  return (
    <>
      {/* Desktop table view */}
      <div className="table-wrapper hidden md:block">
        <Table
          columns={[
            { id: 'name', label: `Guests (${visibleGuests.length})`, width: 'auto' },
            { id: 'dob', label: 'Date of Birth', width: 'auto' },
            { id: 'age', label: 'Age', width: 'auto' },
            { id: 'arrival', label: 'Arrival', width: 'auto' },
            { id: 'arrivalFlight', label: 'Flight N.', width: 'auto' },
            { id: 'departure', label: 'Departure', width: 'auto' },
            { id: 'departureFlight', label: 'Flight N.', width: 'auto' },
          ]}
          messageWhenEmpty="No guest information available"
          sortBy=""
          sortOrder={ESortOrder.ASC}
          onSort={() => {}}
          rows={visibleGuests.map(guest => ({
            id: guest.uuid,
            cells: [
              { id: 'name', value: formatGuestName(guest) },
              { id: 'dob', value: guest.dob ? formatDateDisplay(guest.dob) : '-' },
              { id: 'age', value: calculateGuestAge(guest) },
              {
                id: 'arrival',
                value: guest.arrival_date
                  ? `${formatDateDisplay(guest.arrival_date)} ${formatTime(guest.arrival_time)}`
                  : '-',
              },
              { id: 'arrivalFlight', value: guest.arrival_flightNumber || '-' },
              {
                id: 'departure',
                value: guest.departure_date
                  ? `${formatDateDisplay(guest.departure_date)} ${formatTime(guest.departure_time)}`
                  : '-',
              },
              { id: 'departureFlight', value: guest.departure_flightNumber || '-' },
            ],
          }))}
        />
      </div>

      {/* Mobile expandable table view */}
      <div className="expandable-table-wrapper block md:hidden w-full">
        <ExpandableTable
          data={visibleGuests.map(guest => ({
            id: guest.uuid,
            title: formatGuestName(guest),
            row: {
              id: guest.uuid,
              cells: [
                { title: 'Name', id: 'name', value: formatGuestName(guest) },
                { title: 'D.O.B', id: 'dob', value: guest.dob ? formatDateDisplay(guest.dob) : '-' },
                { title: 'Age', id: 'age', value: calculateGuestAge(guest) },
                {
                  title: 'Arrival',
                  id: 'arrival',
                  value: formatArrivalDeparture(guest.arrival_date, guest.arrival_time, guest.arrival_flightNumber),
                },
                {
                  title: 'Departure',
                  id: 'departure',
                  value: formatArrivalDeparture(
                    guest.departure_date,
                    guest.departure_time,
                    guest.departure_flightNumber
                  ),
                },
              ],
            },
          }))}
        />
      </div>

      {/* Guests More Info */}
      {shouldRenderHideableText(guestsMoreInfo) && (
        <div>
          <HeaderCollapsible
            titleClassname="font-pt-sans text-sm font-normal"
            title="GUESTS INFO"
            childrenClassname="mt-[5px]"
          >
            <p className="m-0 font-pt-sans text-15px text-black whitespace-pre-wrap">
              <HtmlStringRenderer className="m-0" content={guestsMoreInfo!.text} />
            </p>
          </HeaderCollapsible>
        </div>
      )}
    </>
  );
};

// Interface for Further Information Renderer
interface IVoucherFurtherInformationRendererProps {
  hotels: VoucherHotel[];
  extraServices?: BMSVoucherHideableText;
  additionalInformation?: BMSVoucherHideableText;
  membershipBenefits?: BMSVoucherHideableText;
  policiesAndRestrictions?: BMSVoucherHideableText;
  thingsToBeAwareOf?: BMSVoucherHideableText;
  emergencyContact?: string;
}

export const VoucherFurtherInformationRenderer: React.FC<IVoucherFurtherInformationRendererProps> = ({
  hotels,
  extraServices,
  additionalInformation,
  membershipBenefits,
  policiesAndRestrictions,
  thingsToBeAwareOf,
  emergencyContact,
}) => {
  // Check if any section is visible
  const hasVisibleContent =
    shouldRenderHideableText(extraServices) ||
    shouldRenderHideableText(additionalInformation) ||
    shouldRenderHideableText(membershipBenefits) ||
    shouldRenderHideableText(policiesAndRestrictions) ||
    shouldRenderHideableText(thingsToBeAwareOf) ||
    emergencyContact;

  if (!hasVisibleContent) return null;

  return (
    <>
      <h3 className="m-0 font-pt-sans my-0 w-full text-brown-prime pb-2 border-b border-brown-prime uppercase text-17px font-normal">
        Further Information
      </h3>

      <div className="flex flex-col gap-20px w-full">
        {shouldRenderHideableText(extraServices) && (
          <div>
            <HeaderCollapsible
              titleClassname="o:font-normal o:font-pt-sans"
              title="Extra Services"
              childrenClassname="font-pt-sans text-black text-[15px] mt-[5px]"
            >
              <div className="whitespace-pre-wrap">
                <HtmlStringRenderer className="m-0" content={extraServices!.text} />
              </div>
            </HeaderCollapsible>
          </div>
        )}

        {shouldRenderHideableText(additionalInformation) && (
          <div>
            <HeaderCollapsible
              titleClassname="o:font-normal o:font-pt-sans"
              title="Additional Information"
              childrenClassname="font-pt-sans text-black text-[15px] mt-[5px]"
            >
              <div className="whitespace-pre-wrap">
                <HtmlStringRenderer className="m-0" content={additionalInformation!.text} />
              </div>
            </HeaderCollapsible>
          </div>
        )}

        {shouldRenderHideableText(membershipBenefits) && (
          <div>
            <HeaderCollapsible
              titleClassname="o:font-normal o:font-pt-sans"
              title="Membership Benefits"
              childrenClassname="font-pt-sans text-black text-[15px] mt-[5px]"
            >
              <div className="whitespace-pre-wrap">
                <HtmlStringRenderer className="m-0" content={membershipBenefits!.text} />
              </div>
            </HeaderCollapsible>
          </div>
        )}

        {shouldRenderHideableText(policiesAndRestrictions) && (
          <div>
            <HeaderCollapsible
              titleClassname="o:font-normal o:font-pt-sans"
              title="Policies and Restrictions"
              childrenClassname="font-pt-sans text-black text-[15px] mt-[5px]"
            >
              <div className="whitespace-pre-wrap">
                <HtmlStringRenderer className="m-0" content={policiesAndRestrictions!.text} />
              </div>
            </HeaderCollapsible>
          </div>
        )}

        {shouldRenderHideableText(thingsToBeAwareOf) && (
          <div>
            <HeaderCollapsible
              titleClassname="o:font-normal o:font-pt-sans"
              title="Things to Be Aware Of"
              childrenClassname="font-pt-sans text-black text-[15px] mt-[5px]"
            >
              <div className="whitespace-pre-wrap">
                <HtmlStringRenderer className="m-0" content={thingsToBeAwareOf!.text} />
              </div>
            </HeaderCollapsible>
          </div>
        )}

        {emergencyContact && (
          <div>
            <HeaderCollapsible
              titleClassname="o:font-normal o:font-pt-sans"
              title="Emergency Contact"
              childrenClassname="font-pt-sans text-black text-[15px] mt-[5px]"
            >
              <div className="whitespace-pre-wrap">
                <HtmlStringRenderer className="m-0" content={emergencyContact} />
              </div>
            </HeaderCollapsible>
          </div>
        )}

        {/* Announcements Section */}
        <VoucherAnnouncementsRenderer hotels={hotels} />

        <p className="my-0 mt-2 font-pt-sans text-gray-100 first-letter:">
          NOTE. Please review the above carefully and if you notice any error please get back to us as soon as possible.
        </p>
      </div>
    </>
  );
};
